import axios from "axios";
import { route } from "~ziggy-js";
import store from "@/store";
import Router from "@/router";
import { Actions } from "@/store/enums/StoreEnums";
import JwtService from "@/core/services/JwtService";
import ApiService from "@/core/services/ApiService";

/** Match CheckTokenExpiry::IDLE_EXPIRY_MINUTES. */
const IDLE_MS = 30 * 60 * 1000;
/** Requests inside this window count as the user still being here. */
const ACTIVE_WINDOW_MS = 2 * 60 * 1000;
const KEEPALIVE_MS = 60 * 1000;
const TICK_MS = 15 * 1000;
const ACTIVITY_WRITE_MS = 15 * 1000;

const ACTIVITY_KEY = "sl_last_user_activity";
const KEEPALIVE_KEY = "sl_last_keepalive";
const LOGOUT_KEY = "sl_session_logout_at";
/** Cross-tab auth flag (not the API token). */
const AUTH_STATE_KEY = "sl_auth_state";

const readNumber = (key: string) => {
    const value = Number(localStorage.getItem(key) || 0);
    return Number.isFinite(value) ? value : 0;
};

const readActivity = () => {
    const stored = readNumber(ACTIVITY_KEY);
    if (stored > 0) return stored;
    const now = Date.now();
    localStorage.setItem(ACTIVITY_KEY, String(now));
    return now;
};

let lastActivityWrite = 0;

const markActivity = () => {
    const now = Date.now();
    if (now - lastActivityWrite < ACTIVITY_WRITE_MS) return;
    lastActivityWrite = now;
    localStorage.setItem(ACTIVITY_KEY, String(now));
};

/** True when the user typed, clicked, or scrolled in the last two minutes. */
export const isUserRecentlyActive = () => Date.now() - readActivity() < ACTIVE_WINDOW_MS;

type EndSessionReason = "idle" | "session" | "logout" | "device";

/** One logout across tabs. Returns false when another tab already started it. */
export const endSession = (reason: EndSessionReason = "idle") => {
    if (!JwtService.hasAuth()) return false;
    const now = Date.now();
    const previous = readNumber(LOGOUT_KEY);
    if (now - previous < 3000) return false;
    localStorage.setItem(LOGOUT_KEY, String(now));
    localStorage.setItem(AUTH_STATE_KEY, "0");
    localStorage.removeItem(ACTIVITY_KEY);
    store.dispatch(Actions.LOGOUT);
    Router.push({ name: "sign-in", query: { reason } });
    return true;
};

/** Logged out in another tab: clear this tab's state without touching storage again. */
export const followLogoutFromOtherTab = () => {
    if (!store.getters.isUserAuthenticated && Router.currentRoute.value.name === "sign-in") return;
    store.dispatch(Actions.LOGOUT);
    Router.push({ name: "sign-in", query: { reason: "logout" } });
};

/** React to login/logout done in another tab of the same browser. */
const onStorage = (event: StorageEvent) => {
    if (event.key === LOGOUT_KEY && event.newValue) {
        followLogoutFromOtherTab();
        return;
    }
    if (event.key === AUTH_STATE_KEY) {
        if (event.newValue === "0" || event.newValue === null) {
            followLogoutFromOtherTab();
            return;
        }
        // Another tab logged in: refresh features from session-backed verify.
        if (JwtService.hasAuth() || event.newValue) {
            ApiService.setHeader();
            store.dispatch(Actions.VERIFY_AUTH, {});
        }
    }
};

const keepalive = () => {
    const now = Date.now();
    if (now - readNumber(KEEPALIVE_KEY) < KEEPALIVE_MS) return;
    localStorage.setItem(KEEPALIVE_KEY, String(now));
    axios.get(route("auth.verifytoken"), { withCredentials: true }).catch(() => {});
};

const tick = () => {
    if (!JwtService.hasAuth()) return;
    const idle = Date.now() - readActivity();
    if (idle >= IDLE_MS) {
        endSession();
        return;
    }
    if (idle < ACTIVE_WINDOW_MS) keepalive();
};

export const startIdleSession = () => {
    // Drop any legacy id_token left from older builds (keeps auth flag / usr).
    JwtService.getToken();
    if (store.getters.isUserAuthenticated) {
        JwtService.saveToken();
    }

    const events = ["pointerdown", "keydown", "scroll", "touchstart"];
    events.forEach((name) => window.addEventListener(name, markActivity, { passive: true }));
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") tick();
    });
    window.addEventListener("storage", onStorage);
    window.setInterval(tick, TICK_MS);
    tick();
};
